import React from "react";
import { Button, Typography } from "@material-ui/core";
import { alpha, makeStyles } from "@material-ui/core/styles";
import { ArrowRight, CheckCheck, Inbox, MessagesSquare, Search, SlidersHorizontal, UsersRound } from "lucide-react";

import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: theme.spacing(6, 3),
    textAlign: "center",
    animation: "$fadeIn 240ms ease-out",
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    [theme.breakpoints.down("xs")]: { padding: theme.spacing(4, 2) },
  },
  content: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    maxWidth: 400,
    margin: "0 auto",
    gap: theme.spacing(2),
  },
  icon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: theme.spacing(8),
    height: theme.spacing(8),
    borderRadius: theme.spacing(2.5),
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
  },
  // Reutiliza o verde do badge de pendentes em TicketsManagerTabs.
  pendingIcon: {
    color: "#6EC263",
    backgroundColor: alpha("#6EC263", 0.12),
  },
  title: {
    fontSize: "1.125rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  description: {
    fontSize: "0.875rem",
    lineHeight: 1.6,
    color: theme.palette.text.secondary,
    overflowWrap: "anywhere",
  },
  pendingCount: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(1, 2),
    border: 0,
    borderRadius: 999,
    maxWidth: "100%",
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
    fontSize: "0.75rem",
    lineHeight: 1.5,
  },
  pendingAction: {
    cursor: "pointer",
    // Compensa a centralização do flex para manter a borda esquerda e o
    // texto parados enquanto os 24px da seta abrem apenas à direita.
    transition: theme.transitions.create("transform", { duration: theme.transitions.duration.short }),
    "&:hover, &:focus-visible": { transform: `translateX(${theme.spacing(1.5)}px)` },
    "&:hover $pendingArrow, &:focus-visible $pendingArrow": {
      width: theme.spacing(2),
      marginLeft: theme.spacing(1),
      opacity: 1,
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 3,
    },
    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
  },
  pendingArrow: {
    display: "inline-flex",
    alignItems: "center",
    width: 0,
    height: theme.spacing(2),
    marginLeft: 0,
    overflow: "hidden",
    opacity: 0,
    flexShrink: 0,
    "& svg": { flexShrink: 0 },
    transition: theme.transitions.create(["width", "margin-left", "opacity"], { duration: theme.transitions.duration.short }),
    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
  },
  dot: {
    width: theme.spacing(1),
    height: theme.spacing(1),
    marginRight: theme.spacing(1),
    borderRadius: "50%",
    flexShrink: 0,
    backgroundColor: "#6EC263",
  },
  button: {
    textTransform: "none",
    fontWeight: 500,
    borderRadius: theme.spacing(0.75),
    padding: theme.spacing(1, 2),
  },
  "@keyframes fadeIn": {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
}));

const TicketsEmptyState = ({
  status,
  searchParam,
  hasActiveFilters = false,
  showAll = false,
  pendingCount = 0,
  pendingHasMore = false,
  onViewPending,
  onAdjustFilters,
}) => {
  const classes = useStyles();
  const hasSearch = Boolean(searchParam?.trim());
  // Busca e filtros têm prioridade: uma lista restrita vazia não é uma fila zerada.
  const kind = hasSearch ? "search" : hasActiveFilters ? "filtered" : status;
  let Icon = Inbox;
  let title = i18n.t("ticketsList.noTicketsTitle");
  let description = i18n.t("ticketsList.noTicketsMessage");

  if (kind === "search") {
    Icon = Search;
    title = i18n.t("ticketsList.empty.searchTitle");
    description = i18n.t(`ticketsList.empty.${hasActiveFilters ? "searchFilteredDescription" : "searchDescription"}`, { term: searchParam.trim() });
  } else if (kind === "filtered") {
    Icon = SlidersHorizontal;
    title = i18n.t("ticketsList.empty.filteredTitle");
    description = i18n.t("ticketsList.empty.filteredDescription");
  } else if (kind === "open") {
    Icon = MessagesSquare;
    title = i18n.t("ticketsList.empty.openTitle");
    description = i18n.t(`ticketsList.empty.${showAll ? "openAllDescription" : "openDescription"}`);
  } else if (kind === "pending") {
    Icon = CheckCheck;
    title = i18n.t("ticketsList.empty.pendingTitle");
    description = i18n.t("ticketsList.empty.pendingDescription");
  } else if (kind === "groups") {
    Icon = UsersRound;
    title = i18n.t("ticketsList.empty.groupsTitle");
    description = i18n.t("ticketsList.empty.groupsDescription");
  }

  const showPending = kind === "open" && pendingCount > 0;
  const pendingLabel = i18n.t(`ticketsList.empty.${pendingHasMore ? "pendingCountMore" : "pendingCount"}`, { count: pendingCount });

  return (
    <div className={classes.root} role="status" aria-live="polite" aria-atomic="true">
      <div className={classes.content}>
        <div className={`${classes.icon} ${kind === "pending" ? classes.pendingIcon : ""}`}>
          <Icon size={28} strokeWidth={1.75} aria-hidden="true" />
        </div>
        <Typography component="h2" className={classes.title}>{title}</Typography>
        <Typography component="p" className={classes.description}>{description}</Typography>
        {showPending && (
          <Typography
            component={onViewPending ? "button" : "p"}
            type={onViewPending ? "button" : undefined}
            className={`${classes.pendingCount} ${onViewPending ? classes.pendingAction : ""}`}
            onClick={onViewPending}
            aria-label={onViewPending ? `${pendingLabel}. ${i18n.t("ticketsList.empty.viewPending")}` : undefined}
          >
            <span className={classes.dot} aria-hidden="true" />
            <span>{pendingLabel}</span>
            {onViewPending && (
              <span className={classes.pendingArrow} aria-hidden="true">
                <ArrowRight size={16} strokeWidth={1.75} />
              </span>
            )}
          </Typography>
        )}
        {hasActiveFilters && onAdjustFilters && (
          <Button color="primary" className={classes.button} onClick={onAdjustFilters}>
            {i18n.t("ticketsList.empty.adjustFilters")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default TicketsEmptyState;
